import { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  getTask,
  createTaskForChannel,
  updateTask,
  deleteAction,
  addTaskReply,
  executeTask,
  getTaskOptions,
} from "../../features/tasks/taskSlice";
import { fetchSettingDetailsByUserId } from "../../features/settings/settingsSlice";
import { getParticipants } from "../../features/participant/participantSlice";
import {
  sendIntegrationMessage,
  sendMessageReply,
  fetchConversationBySettingId,
  updateSyncedMessageInConversation,
} from "../../features/integration-setting/integrationSettingSlice";
import {
  ALL_CONVERSATION_INTEGRATIONS,
  COMMENT_PLATFORMS,
} from "./Conversation.utils";
import { INTEGRATION_ENUM } from "../../constants/constants";

export const getGroupKey = (group) => group?.id || group?._id || group?.label;
export const getItemId = (item) => item?.uuid || item?._id || item?.externalMessageId;

export function useReplyDrafts() {
  const [openId, setOpenId] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [sendingIds, setSendingIds] = useState(new Set());

  const toggle = (id) => setOpenId((cur) => (cur === id ? null : id));
  const change = (id, value) => setDrafts((prev) => ({ ...prev, [id]: value }));

  const startSending = (id) => setSendingIds((prev) => new Set(prev).add(id));
  const stopSending = (id) =>
    setSendingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  const clear = (id) => {
    setDrafts((prev) => ({ ...prev, [id]: "" }));
    setOpenId(null);
  };

  return {
    openId,
    drafts,
    sendingIds,
    toggle,
    change,
    startSending,
    stopSending,
    clear,
  };
}

export default function useConversation({
  group,
  isLoading,
  integrationSettingId = null,
  taskUuid = null,
}) {
  const dispatch = useDispatch();

  const [draft, setDraft] = useState("");
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [justAdded, setJustAdded] = useState(null);
  const [addingTaskId, setAddingTaskId] = useState(null);
  const [platformMenuOpenId, setPlatformMenuOpenId] = useState(null);
  const [taskPlatformMenuOpenId, setTaskPlatformMenuOpenId] = useState(null);
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [isExecutingPlatform, setIsExecutingPlatform] = useState(false);

  const reply = useReplyDrafts();
  const taskReply = useReplyDrafts();

  const [executeModal, setExecuteModal] = useState({
    open: false,
    item: null,
    authorName: null,
    platform: null,
    action: null,
  });

  const fetchedTaskOptionsRef = useRef(new Set());

  const {
    task,
    loading: taskLoading,
    taskOptions,
  } = useSelector((state) => state.task);
  const { sendingMessage } = useSelector((state) => state.integrationSetting);
  const { userSettings } = useSelector((state) => state.setting);
  const { user } = useSelector((state) => state.user);
  const { participants: allParticipantsRaw = [] } = useSelector(
    (state) => state.participants,
  );

  const allParticipants = useMemo(
    () => allParticipantsRaw.filter((p) => p.platforms?.length > 0),
    [allParticipantsRaw],
  );

  useEffect(() => {
    if (!user?.org) return;

    if (!userSettings?.length) {
      dispatch(fetchSettingDetailsByUserId({ accountId: user.org }));
    }
    if (!allParticipantsRaw?.length) {
      dispatch(getParticipants());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.org]);

  const groupKey = getGroupKey(group);
  const platformLower = String(group?.platform).toLowerCase();
  const isCrisp = platformLower === INTEGRATION_ENUM.CRISP;
  const isIntercom = platformLower === INTEGRATION_ENUM.INTERCOM;
  const isAllConversation =
    ALL_CONVERSATION_INTEGRATIONS.includes(platformLower);

  const isMailIntegration =
    platformLower === INTEGRATION_ENUM.GMAIL ||
    platformLower === INTEGRATION_ENUM.OUTLOOK;
  const isCommentIntegration =
    platformLower === INTEGRATION_ENUM.INSTAGRAM_COMMENTS ||
    platformLower === INTEGRATION_ENUM.FACEBOOK_COMMENTS;

  const platforms = useMemo(() => {
    const seen = new Set();
    return (userSettings || []).filter((s) => {
      const name = s?.assistantId?.name;
      if (!name || seen.has(s.uuid)) return false;
      seen.add(s.uuid);
      return true;
    });
  }, [userSettings]);

  const getSettingUuid = () => group?.settingId || integrationSettingId;
  const getRecipientId = (item) =>
    isAllConversation
      ? (item?.author?.username ??
        selectedParticipantGroup?.recipientId ??
        item?.author?.externalUserId ??
        item?.author?.name ??
        group?.recipientId ??
        null)
      : null;
  const refreshTask = () => {
    if (taskUuid) dispatch(getTask(taskUuid));
  };

  useEffect(() => {
    setSelectedParticipant(null);
    setIsComposerOpen(false);
    reply.toggle(null);
    taskReply.toggle(null);
    setPlatformMenuOpenId(null);
    setTaskPlatformMenuOpenId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupKey]);

  useEffect(() => {
    if (taskUuid) dispatch(getTask(taskUuid));
  }, [taskUuid, dispatch]);

  const authorMap = useMemo(() => group?.authorMap || {}, [group]);

  const participantGroups = useMemo(() => {
    if (!group?.participantGroups?.length) return [];

    const messagesById = new Map(
      (group.messages || []).map((m) => [m.externalMessageId || m.uuid, m]),
    );

    return group.participantGroups.map((integration) => ({
      ...integration,
      participants: integration.participants.map((participant) => ({
        ...participant,
        messages: participant.messageIds
          .map((id) => messagesById.get(id))
          .filter(Boolean),
      })),
    }));
  }, [group]);

  const selectedParticipantGroup = useMemo(() => {
    if (!selectedParticipant) return null;
    const integrationGroup = participantGroups.find(
      (integration) =>
        integration.integrationKey === selectedParticipant.integrationKey,
    );
    if (!integrationGroup) return null;
    return (
      integrationGroup.participants.find(
        (participant) =>
          participant.participantKey === selectedParticipant.participantKey,
      ) || null
    );
  }, [participantGroups, selectedParticipant]);

  const selectedParticipantMessages = useMemo(
    () => selectedParticipantGroup?.messages || [],
    [selectedParticipantGroup],
  );

   const getChannelId = (message) => {
    if (isCrisp || isIntercom) {
      return (
        message?.externalThreadId ||
        selectedParticipantMessages?.[0]?.externalThreadId ||
        null
      );
    }
    if (COMMENT_PLATFORMS.includes(platformLower)) {
      return (
        selectedParticipantMessages?.[0]?.externalThreadId ||
        group?.channelId ||
        group?.externalId ||
        group?.id ||
        group?.key
      );
    }

    return group?.channelId || group?.externalId || group?.id || group?.key;
  };

  const tasks = useMemo(() => {
     if (!taskUuid || task?.uuid !== taskUuid || !task?.actions?.length) {
      return [];
    }
    return task.actions.map((action) => ({
      ...action,
      taskDocUuid: task.uuid,
      integrationSettingId: task.integrationSettingId || integrationSettingId,
    }));
  }, [task, integrationSettingId, taskUuid]);

  const addedMessageIds = useMemo(
    () => new Set(tasks.map((t) => String(t.sourceMessageId)).filter(Boolean)),
    [tasks],
  );

  const selectedThreadIds = useMemo(() => {
    if (!selectedParticipantMessages.length) return new Set();
    const ids = new Set();
    selectedParticipantMessages.forEach((message) => {
      if (message.externalMessageId) ids.add(message.externalMessageId);
      (message.threads || []).forEach((t) => {
        if (t.externalMessageId) ids.add(t.externalMessageId);
      });
    });
    return ids;
  }, [selectedParticipantMessages]);

  const selectedMessageTasks = useMemo(() => {
    if (!selectedParticipant) return tasks;
    return tasks.filter((t) => selectedThreadIds.has(t.sourceMessageId));
  }, [tasks, selectedParticipant, selectedThreadIds]);

  const selectedMessageAction = useMemo(() => {
    if (!selectedParticipantMessages.length) return null;
    const itemId = selectedParticipantMessages[0].externalMessageId;
    return tasks.find((t) => t.sourceMessageId === itemId) || null;
  }, [tasks, selectedParticipantMessages]);

  const addTaskFromItems = async (items, contextLabel) => {
    const conversationItems = Array.isArray(items) ? items : [items];
    const sourceItem = conversationItems
      .filter(Boolean)
      .slice()
      .sort((a, b) => new Date(b.sentAt || 0) - new Date(a.sentAt || 0))[0];
    const sourceId = sourceItem?.externalMessageId;

    if (!taskUuid) {
      toast.error("Select a specific conversation to add a task.");
      return;
    }
    if (!sourceId) {
      toast.error("Unable to determine source message for this item.");
      return;
    }
    if (addedMessageIds.has(sourceId)) return;

    setAddingTaskId(sourceId);

    const sourceChannelId = getChannelId();
    if (!sourceChannelId) {
      setAddingTaskId(null);
      toast.error("Unable to determine conversation channel.");
      return;
    }

    const taskData = {
      messages: conversationItems.filter(Boolean),
      authorMap,
      sourceMessageId: sourceId,
      sourceChannelId,
      contextLabel,
    };

    const result = await dispatch(createTaskForChannel({ taskUuid, taskData }));

    if (createTaskForChannel.fulfilled.match(result)) {
      if (result.payload?.noTasksExtracted) {
        toast.warning(
          result.payload.message ||
            "No task could be extracted from this message.",
        );
      } else {
        toast.success("Added to tasks");
        refreshTask();
        setJustAdded(sourceId);
        setTimeout(
          () => setJustAdded((cur) => (cur === sourceId ? null : cur)),
          1200,
        );
      }
    } else {
      toast.error(result.payload?.message || "Failed to add task");
    }

    setAddingTaskId(null);
  };

  const handleSelectParticipant = (integrationKey, participantKey) => {
    setIsComposerOpen(false);
    setSelectedParticipant({ integrationKey, participantKey });
  };

  const handleAddTask = (items) => addTaskFromItems(items, "conversation");
  const handleAddThreadTask = (thread) => addTaskFromItems(thread, "reply");

  const handleSelectPlatform = (item, authorName, platformOption) => {
    setPlatformMenuOpenId(null);
    const itemId = getItemId(item);
    const action = tasks.find((t) => t.sourceMessageId === itemId) || null;

    if (!action) {
      toast.error(
        "Add this message to tasks first, then send it to a platform.",
      );
      return;
    }

    setExecuteModal({
      open: true,
      item,
      authorName,
      platform: platformOption,
      action,
    });
  };

  const handleSelectPlatformForTask = (taskItem, platformOption) => {
    setTaskPlatformMenuOpenId(null);
    setExecuteModal({
      open: true,
      item: { text: taskItem.description || taskItem.name },
      authorName: null,
      platform: platformOption,
      action: taskItem,
    });
  };

  const closeExecuteModal = () =>
    setExecuteModal({
      open: false,
      item: null,
      authorName: null,
      platform: null,
      action: null,
    });

  const handleTaskFieldUpdate = async (uuid, payload) => {
    const taskItem = tasks.find((t) => t.uuid === uuid);
    if (!taskItem) return;

    const result = await dispatch(
      updateTask({ uuid, taskId: taskItem.taskDocUuid, ...payload }),
    );

    if (updateTask.fulfilled.match(result)) {
      refreshTask();
      setExecuteModal((prev) =>
        prev.action?.uuid === uuid
          ? { ...prev, action: { ...prev.action, ...payload } }
          : prev,
      );
    } else {
      toast.error("Failed to update task");
    }
  };

  const handleAssignTaskMembers = (taskItem, memberUuids) =>
    handleTaskFieldUpdate(taskItem.uuid, { members: memberUuids });

  useEffect(() => {
    if (!executeModal.open || !executeModal.platform || !executeModal.action)
      return;

    const platformType = executeModal.platform?.assistantId?.platform?.type;
    const settingUuid = executeModal.platform?.uuid;
    const action = executeModal.action;

    if (action.settingId !== settingUuid) {
      handleTaskFieldUpdate(action.uuid, { settingId: settingUuid });
    }

    if (platformType && platformType !== "unknown") {
      const key = `${platformType}_${settingUuid}`;
      if (!fetchedTaskOptionsRef.current.has(key)) {
        fetchedTaskOptionsRef.current.add(key);
        dispatch(
          getTaskOptions({
            settingId: settingUuid,
            platformType,
            meetingId: action.taskDocUuid,
          }),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executeModal.open, executeModal.platform?.uuid]);

  const handleExecuteOnPlatform = async () => {
    const { action } = executeModal;
    if (!action) return;

    setIsExecutingPlatform(true);
    const result = await dispatch(
      executeTask({ taskData: { ...action, taskId: action.taskDocUuid } }),
    );

    if (executeTask.fulfilled.match(result) && result.payload?.success) {
      toast.success("Task executed successfully");
      refreshTask();
      closeExecuteModal();
    } else {
      toast.error(result.payload?.message || "Failed to execute task");
    }

    setIsExecutingPlatform(false);
  };

  const handleSendReply = async (item, id) => {
    const text = (reply.drafts[id] || "").trim();
    if (!text) return;

    const messageId = item.externalMessageId;
    const settingUuid = getSettingUuid();
    const channelId = getChannelId(item);
    const recipientId = getRecipientId(item);

    if (!settingUuid || !channelId || !messageId) {
      toast.error("Unable to determine conversation to send reply.");
      return;
    }
    if (isAllConversation && !recipientId) {
      toast.error("Unable to determine recipient for this reply.");
      return;
    }

    reply.startSending(id);

    const result = await dispatch(
      sendMessageReply({
        integrationSettingUuid: settingUuid,
        messageId,
        channelId,
        recipientId,
        message: text,
      }),
    );

    reply.stopSending(id);

    if (sendMessageReply.fulfilled.match(result)) {
      toast.success("Reply sent");
      reply.clear(id);
      if (settingUuid) {
        dispatch(fetchConversationBySettingId(settingUuid));
      }
    } else {
      toast.error(result.payload?.message || "Failed to send reply");
    }
  };

  const handleSend = async () => {
    const text = draft.trim();
    if (!text) return;

    const settingUuid = getSettingUuid();
    const channelId = getChannelId(selectedParticipantMessages?.[0]);
    const recipientId = getRecipientId(selectedParticipantGroup);

    if (!settingUuid || !channelId) {
      toast.error("Unable to determine conversation to send message.");
      return;
    }
    if (isAllConversation && !recipientId) {
      toast.error("Unable to determine recipient for this reply.");
      return;
    }

    const result = await dispatch(
      sendIntegrationMessage({
        integrationSettingUuid: settingUuid,
        channelId,
        recipientId,
        message: text,
      }),
    );

    if (sendIntegrationMessage.fulfilled.match(result)) {
      toast.success("Message sent");
      setDraft("");
      setIsComposerOpen(false);
      if (settingUuid) {
        dispatch(fetchConversationBySettingId(settingUuid));
      }
    } else {
      toast.error(result.payload?.message || "Failed to send message");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const toggleTaskCompleted = async (taskItem) => {
    const nextStatus =
      taskItem.status === "completed" ? "pending" : "completed";
    const result = await dispatch(
      updateTask({
        uuid: taskItem.uuid,
        taskId: taskItem.taskDocUuid,
        status: nextStatus,
      }),
    );
    if (updateTask.fulfilled.match(result)) refreshTask();
    else toast.error("Failed to update task");
  };

  const removeTask = async (taskItem) => {
    const targetTaskId = taskItem.taskDocUuid;
    if (!targetTaskId) {
      toast.error("Unable to determine task for this item.");
      return;
    }

    const result = await dispatch(
      deleteAction({ taskId: targetTaskId, actionUuid: taskItem.uuid }),
    );

    if (deleteAction.fulfilled.match(result)) {
      toast.success("Task removed successfully.");
      refreshTask();
    } else {
      toast.error(result.payload?.message || "Failed to remove task");
    }
  };

  const handleSendTaskReply = async (taskItem) => {
    const text = (taskReply.drafts[taskItem.uuid] || "").trim();
    if (!text) return;

    taskReply.startSending(taskItem.uuid);

    const result = await dispatch(
      addTaskReply({
        taskId: taskItem.taskDocUuid,
        actionId: taskItem.uuid,
        reply: text,
      }),
    );

    taskReply.stopSending(taskItem.uuid);

    if (addTaskReply.fulfilled.match(result)) {
      toast.success("Reply added");
      refreshTask();
      const updatedMessage = result.payload?.data;
      if (updatedMessage) {
        dispatch(updateSyncedMessageInConversation(updatedMessage));
      }
      const settingUuid = getSettingUuid();
      if (settingUuid) {
        dispatch(fetchConversationBySettingId(settingUuid));
      }
      taskReply.clear(taskItem.uuid);
    } else {
      toast.error(result.payload?.message || "Failed to add reply");
    }
  };

  const modalPlatformType = executeModal.platform?.assistantId?.platform?.type;
  const modalKey =
    modalPlatformType && executeModal.platform?.uuid
      ? `${modalPlatformType}_${executeModal.platform.uuid}`
      : null;
  const modalTaskOptions = modalKey ? taskOptions?.[modalKey] || {} : {};

  const meta = {
    group,
    isLoading,
    isMailIntegration,
    isCommentIntegration,
    sendingMessage,
    showGroupLabel: false,
  };

  const listData = {
    participantGroups,
    authorMap,
    selectedParticipant,
    onSelectParticipant: handleSelectParticipant,
    showGroupLabel: meta.showGroupLabel,
  };

  const threadData = {
    selectedParticipant,
    selectedParticipantGroup,
    selectedParticipantMessages,
    selectedMessageAction,
    authorMap,
    isComposerOpen,
    toggleComposer: () => {
      setSelectedParticipant((cur) => cur);
      setIsComposerOpen((o) => !o);
    },
    closeComposer: () => setIsComposerOpen(false),
    messageInput: {
      draft,
      setDraft,
      onSend: handleSend,
      onKeyDown: handleKeyDown,
    },
    taskActions: {
      addedMessageIds,
      addingTaskId,
      justAdded,
      onAddTask: handleAddTask,
      onAddThreadTask: handleAddThreadTask,
    },
    replyProps: {
      replyOpenId: reply.openId,
      replyDrafts: reply.drafts,
      onToggleReply: reply.toggle,
      onReplyChange: reply.change,
      onSendReply: handleSendReply,
      sendingReplyIds: reply.sendingIds,
    },
    platformProps: {
      platforms,
      platformMenuOpenId,
      onTogglePlatformMenu: (id) =>
        setPlatformMenuOpenId((cur) => (cur === id ? null : id)),
      onSelectPlatform: handleSelectPlatform,
    },
  };

  const tasksData = {
    tasks: selectedMessageTasks,
    allTasks: tasks,
    allTasksCount: tasks.length,
    hasSelection: !!selectedParticipantGroup,
    taskLoading,
    authorMap,

    onToggleCompleted: toggleTaskCompleted,
    onAssignMembers: handleAssignTaskMembers,
    onRemove: removeTask,
    replyProps: {
      replyOpenId: taskReply.openId,
      replyDrafts: taskReply.drafts,
      onToggleReply: taskReply.toggle,
      onReplyChange: taskReply.change,
      onSendReply: handleSendTaskReply,
      sendingReplyIds: taskReply.sendingIds,
    },
    platformProps: {
      platforms,
      platformMenuOpenId: taskPlatformMenuOpenId,
      onTogglePlatformMenu: (id) =>
        setTaskPlatformMenuOpenId((cur) => (cur === id ? null : id)),
      onSelectPlatform: handleSelectPlatformForTask,
    },
  };

  const modal = {
    modalState: {
      show: executeModal.open,
      platform: executeModal.platform,
      action: executeModal.action,
      item: executeModal.item,
      authorName: executeModal.authorName,
    },
    data: {
      participants: allParticipants,
      settings: platforms,
      taskOptions: modalTaskOptions,
    },
    handlers: {
      onClose: closeExecuteModal,
      onFieldUpdate: handleTaskFieldUpdate,
      onExecute: handleExecuteOnPlatform,
    },
    isExecuting: isExecutingPlatform,
  };

  return { meta, listData, threadData, tasksData, modal };
}
